import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react'

type EventListenerCallback = () => void

function dispatchStorageEvent(key: string, newValue: null | string) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }))
}

const setLocalStorageItem = (key: string, value: unknown) => {
  const stringifiedValue = JSON.stringify(value)
  window.localStorage.setItem(key, stringifiedValue)
  dispatchStorageEvent(key, stringifiedValue)
}

const useLocalStorageSubscribe = (callback: EventListenerCallback) => {
  window.addEventListener('storage', callback)
  return () => window.removeEventListener('storage', callback)
}

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key)
  dispatchStorageEvent(key, null)
}

const getLocalStorageItem = (key: string) => window.localStorage.getItem(key)

// Got the implementation from: https://github.com/uidotdev/usehooks/blob/90fbbb4cc085e74e50c36a62a5759a40c62bb98e/index.js#L616
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const getSnapshot = () => getLocalStorageItem(key)

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot)

  const setState = useCallback(
    (value: unknown) => {
      try {
        const nextState =
          typeof value === 'function' && store !== null
            ? value(JSON.parse(store))
            : value

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key)
        } else {
          setLocalStorageItem(key, nextState)
        }
      } catch (e) {
        console.warn(e)
      }
    },
    [key, store]
  )

  useEffect(() => {
    if (
      getLocalStorageItem(key) === null &&
      typeof initialValue !== 'undefined'
    ) {
      setLocalStorageItem(key, initialValue)
    }
  }, [key, initialValue])

  return [store ? JSON.parse(store) : initialValue, setState]
}
